<script>
import {
  enableChat,
  theme,
  isMassiveEnabled,
  isAppointmentsEnabled,
  isProgramsEnabled,
  isProtocolsEnabled,
  arePrescriptionsForDoctorOnly,
  isPrescriptionsHiddenForDoctor,
  areFollowupsRestrictedForBackoffice,
  areProtocolsRestrictedForBackoffice,
  areValidationsRestrictedForBackoffice,
  isOrdersCrmEnabled,
} from "@/services/config";
import { mapGetters } from "vuex";

const PAGES_WITHOUT_SUBNAV = ["appointment", "patient-dashboard"];

export default {
  name: "SubNav",

  props: {
    nav: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      enableChat,
      theme,
      isMassiveEnabled,
      updateTitle: undefined,
      isAppointmentsEnabled,
      isProgramsEnabled,
      isPaymentEnabled: theme === "dash",
      isOrdersCrmEnabled,
    };
  },

  created() {
    this.$bus.$on("update-title", (title) => {
      this.updateTitle = title;
    });
  },

  watch: {
    "$route.name"() {
      this.updateTitle = undefined;
    },
  },

  methods: {
    isRoute(name) {
      return this.$route.name === name;
    },

    isInRouteGroup(baseName) {
      const current = this.$route.name || "";
      const byName = current === baseName || current.startsWith(`${baseName}-`);
      const byPath = (this.$route.path || "").startsWith(`/${baseName}`);
      return byName || byPath;
    },

    isRouteOneOf(routeNames = []) {
      const current = this.$route.name || "";
      return routeNames.includes(current);
    },

    getSectionTitle() {
      switch (this.$route.name) {
        case "dashboard-conversations":
          return "Conversaciones";

        case "dashboard-conversations-unreplied":
          return "Auditoría Chat";

        case "settings":
          return "Configuración";

        // case "users-list":
        //   return "Usuarios";

        case "profile":
          return "Perfil";

        case "appointment-schedule":
          return "Mis atenciones del día";

        case "validation":
          return "Validaciones";

        case "patient-dashboard":
          return undefined;

        case "settings-massive":
          return "Carga Masiva";

        default:
          return "";
      }
    },
  },

  computed: {
    ...mapGetters([
      "isAdmin",
      "isAssistant",
      "isDoctor",
      "isManager",
      "isPatient",
      "isAdmissionist",
      "user",
    ]),

    // El admin siempre ve Estadísticas; el asistente, solo si tiene el permiso
    // habilitado (statistics.view, análogo al CRM de órdenes).
    isStatisticsVisible() {
      return (
        this.isAdmin ||
        (this.user?.permissions || []).includes("statistics.view")
      );
    },

    isFollowupsVisible() {
      if (this.isPatient || this.isAdmissionist || this.isDoctor) return false;
      if (areFollowupsRestrictedForBackoffice) {
        return !this.isAdmin && !this.isAssistant && !this.isManager;
      }
      return true;
    },

    isProtocolsVisible() {
      if (!isProtocolsEnabled) return false;
      if (this.isPatient || this.isAdmissionist || this.isDoctor) return false;
      if (areProtocolsRestrictedForBackoffice) {
        return !this.isAdmin && !this.isAssistant && !this.isManager;
      }
      return true;
    },

    isValidationsVisible() {
      if (this.isPatient || this.isAdmissionist || this.isDoctor) return false;
      if (areValidationsRestrictedForBackoffice) {
        return !this.isAdmin && !this.isAssistant && !this.isManager;
      }
      return true;
    },

    isPrescriptionsVisible() {
      if (isPrescriptionsHiddenForDoctor && this.isDoctor) return false;
      if (arePrescriptionsForDoctorOnly) {
        return this.isDoctor;
      }
      return !this.isDoctor;
    },

    hasSubNav() {
      return !PAGES_WITHOUT_SUBNAV.includes(this.$route.name);
    },

    isTreatmentSubNavActive() {
      return (
        this.$route.name === "treatment" ||
        this.$route.name === "treatment-tasks"
      );
    },

    isLabelSubNavActive() {
      return this.$route.name === "labels";
    },

    isConfigSubNavActive() {
      return (
        this.$route.name !== "treatment-tasks" &&
        (this.$route.name.includes("treatment-") ||
          this.$route.name === "create-vital" ||
          this.$route.name === "update-vital")
      );
    },

    isValidationSubNavActive() {
      return (
        this.$route.name === "validation" ||
        this.$route.name === "validation-history"
      );
    },
  },
};
</script>

<template lang="pug">
    section.subnav-container
      nav.topSubnav__list(v-if="!isRoute('home')")
        router-link.topSubnav__link.has-subnav(v-if="!isPatient && !isDoctor" :to="{ name: 'home' }", :class="{ 'is-not-active': !isRoute('home') }")
          micon(name="house")
          p Home
        router-link.topSubnav__link(v-if="isFollowupsVisible" :to="{ name: 'dashboard-alerts' }", :class="{ 'is-not-active': !isRouteOneOf(['dashboard-alerts','assistant-evaluation']) }")
          micon(name="pending_actions")
          p Seguimientos
        router-link.topSubnav__link(v-if="!isPatient" :to="{ name: 'patient' }" v-auth="'patient.view'" :class="{ 'is-not-active': !isInRouteGroup('patient') }")
          micon(name="people")
          p Pacientes
        router-link.topSubnav__link.has-subnav(v-if="isProtocolsVisible" :to="{ name: 'treatment' }" :class="{ 'is-not-active': !isInRouteGroup('treatment') }")
          micon(name="assignment")
          p Protocolos
        router-link.topSubnav__link(v-if='isAppointmentsEnabled && !isPatient' :to="{ name: 'appointment-calendar' }" :class="{ 'is-not-active': !isRouteOneOf(['appointment-calendar','appointment-agendas','appointment-relocation']) }")
          micon(name="calendar")
          p Agenda
        router-link.topSubnav__link(v-if='isAppointmentsEnabled && isDoctor' :to="{ name: 'appointment-schedule' }" :class="{ 'is-not-active': !isRoute('appointment-schedule') }")
          micon(name="list")
          p Atenciones
        router-link.topSubnav__link(v-if="isValidationsVisible" :to="{ name: 'validation' }" :class="{ 'is-not-active': !isInRouteGroup('validation') }")
          micon(name="check")
          p Validaciones
        router-link.topSubnav__link(v-if='isPrescriptionsVisible' :to="{ name: 'prescription' }" :class="{ 'is-not-active': !isInRouteGroup('prescription') }")
          micon(name="medication")
          p Prescripciones
        router-link.topSubnav__link(v-if="isAdmin && isProgramsEnabled" :to="{ name: 'administration' }", :class="{ 'is-not-active': !isRoute('administration') }")
          micon(name="admin_panel_settings")
          p Programas
        router-link.topSubnav__link(v-if="isStatisticsVisible" :to="{ name: 'stats' }", :class="{ 'is-not-active': !isRoute('stats') }")
          micon(name="insert_chart_outlined")
          p Estadísticas
        router-link.topSubnav__link(v-if="isAdmin" :to="{ name: 'users-list' }", :class="{ 'is-not-active': !isRoute('users-list') }", v-auth="['doctor.query', 'manager.query', 'assistan.query', 'admin.query']")
          micon(name="admin_panel_settings")
          p Usuarios
        router-link.topSubnav__link.has-subnav(v-if="isAdmin" :to="{ name: 'settings' }", :class="{ 'is-not-active': !isRoute('settings') }")
          micon(name="settings")
          p Configuración
        router-link.topSubnav__link(v-if='isAdmin' :to="{ name: 'contracts' }" :class="{ 'is-not-active': !isInRouteGroup('contracts') }")
          micon(name="description")
          p Contratos
        router-link.topSubnav__link(v-if='isAdmin' :to="{ name: 'prescription-queue' }" :class="{ 'is-not-active': !isRouteOneOf(['prescription-queue', 'prescription-history']) }")
          micon(name="medication")
          p Bandeja de recetas
        router-link.topSubnav__link(v-if="isOrdersCrmEnabled" v-auth="'orders-crm.view'" :to="{ name: 'orders-crm' }" :class="{ 'is-not-active': !isRoute('orders-crm') }")
          micon(name="assignment_turned_in")
          p CRM de órdenes
        router-link.topSubnav__link(v-if="isAdmin || isAssistant" :to="{ name: 'doctor-location' }" :class="{ 'is-not-active': !isRoute('doctor-location') }")
          micon(name="location_on")
          p Ubicación médicos
        router-link.topSubnav__link(v-if="isPatient" :to="{ name: 'home' }" :class="{ 'is-not-active': nav !== 'dashboard' }")
          micon(name="person")
          p Mi Panel
        //- router-link.topSubnav__link(v-if="isPatient" :to="{ name: 'appointment' }")
        //-   micon(name="calendar")
        //-   strong Buscar Citas
        router-link.topSubnav__link(v-if='isPaymentEnabled && (isDoctor || isPatient)' :to="{ name: 'payments-list' }")
          micon(name="history")
          p {{ isPatient ? "Atenciones" : "Pagos" }}

      //-   
      //-   div(v-if="isRoute('schedule')")
      //-     select
      //-       option(value="todos" selected) Todos los consultorios
      //-       option(value="consultorio1") Consultorio 1
      //-       option(value="consultorio2") Consultorio 2
      //-     micon.plus(name="add")
      
      //-         //- router-link.subnav__item(:to="{ name: 'label' }", :class="{ 'router-link-exact-active': isLabelSubNavActive }", exact) Etiquetas 
      //-
      //-
      //-   //- nav.subnav__list(v-else-if="nav=== 'appointment' && !isPatient")
      //-   //-   router-link.subnav__item(v-if="isDoctor" :to="{ name: 'schedule' }", :class="{'active': isRoute('schedule')}") Mi día
      //-   //-   router-link.subnav__item(:to="{ name: 'appointment' }", :class="{'active': isRoute('appointment')}") Calendario
      //-   //-   router-link.subnav__item(v-if="isAdmin || isDoctor" :to="{ name: 'appointment-agendas' }", :class="{'active': isRoute('appointment-agendas')}") {{ isDoctor ? 'Mis agendas' : 'Agendas' }}
      //-   //-   router-link.subnav__item(v-if="!isPatient" :to="{ name: 'appointment-relocation' }", :class="{'active': isRoute('appointment-relocation')}") Citas a reprogramar
      //-   nav.subnav__list(v-else-if="nav=== 'appointment' && isPatient")
      //-     router-link.subnav__item(:to="{ name: 'appointment-calendar' }", :class="{'active': isRoute('appointment-calendar')}") Buscar citas
      //-     router-link.subnav__item(v-if="isPatient" :to="{ name: 'appointment-schedule' }", :class="{'active': isRoute('appointment-schedule')}") Mis citas
</template>

<style lang="scss" scoped>
.subnav {
  .headline__title {
    position: absolute;
    left: 15px;
    margin-right: 30px;
  }
  .plus {
    background-color: #556ba3;
    border-radius: 100%;
    fill: white;
    margin: 0 15px;
    font-family: "Roboto", Helvetica, Arial, sans-serif;
  }
}
.topSubnav__list {
  display: flex;
  overflow-x: auto;
  gap: 1rem;
  padding: 0.5rem 1rem;
  justify-content: flex-start;

  @media (min-width: 1200px) {
    justify-content: center;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
  }
}

.topSubnav__link {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  padding: 0.75rem;
  box-shadow: 0 2px 5px #0001;
  text-align: center;
  color: #040055;
  text-decoration: none;
  font-weight: bold;
  font-size: 0.9rem;
  transition: 0.2s ease;
  &:hover {
    background: #f1f4f9;
  }

  micon {
    font-size: 1.5rem;
  }
}
</style>
